/** Phase 3A — SuggestedMods live diff preview.
 *
 * When the critic proposes changes, show the projected before-vs-after risk
 * picture BEFORE the user applies anything. Backed by the non-mutating
 * `POST /api/risk/preview-modification` endpoint.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ArrowRight, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

const STATE_TONE = {
  improved:            "pnl-up",
  worsened:            "pnl-down",
  unchanged:           "text-muted-foreground",
  changed:             "text-signal-yellow",
  newly_compliant:     "pnl-up",
  still_compliant:     "pnl-up",
  newly_noncompliant:  "pnl-down",
  still_noncompliant:  "pnl-down",
};

const FIELD_LABEL = {
  entry: "Entry",
  stop: "Stop",
  target: "Target",
  qty: "Quantity",
  order_value: "Order Value",
  max_loss: "Max Loss",
  potential_profit: "Potential Profit",
  reward_risk: "Reward-to-Risk",
  sector_exposure_pct: "Sector Exposure",
  portfolio_cash_remaining: "Portfolio Cash",
};

function fmt(key, v) {
  if (v == null) return "—";
  if (key === "reward_risk") return v.toFixed(2);
  if (key === "sector_exposure_pct") return `${v}%`;
  if (key === "qty") return v;
  if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${Number(v).toFixed(2)}`;
}

export default function SuggestedModsDiff({
  proposalId, planId, portfolioKey,
  currentValues, draftValues,
  onApply, onReject,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!proposalId || !planId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.post("/risk/preview-modification", {
      proposal_id: proposalId,
      plan_id: planId,
      current: currentValues,
      draft: draftValues,
      portfolio_key: portfolioKey,
    })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Preview failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [proposalId, planId, portfolioKey, JSON.stringify(currentValues), JSON.stringify(draftValues)]);

  if (loading && !preview) {
    return (
      <div className="border border-border/50 p-3 text-xs" data-testid="mod-diff-loading">
        <Loader2 size={12} className="inline animate-spin mr-2"/>Projecting risk impact…
      </div>
    );
  }
  if (error) {
    return (
      <div className="border border-signal-red/40 p-3 text-xs pnl-down" role="alert" data-testid="mod-diff-error">
        {error}
      </div>
    );
  }
  if (!preview) return null;

  const verdictTone = STATE_TONE[preview.verdict_state] || "";
  const verdictLabel = {
    newly_compliant:    "Newly Approved",
    still_compliant:    "Still Approved",
    newly_noncompliant: "Now Rejected",
    still_noncompliant: "Still Rejected",
  }[preview.verdict_state] || preview.verdict_state;

  // Only show fields that actually change or that speak to compliance.
  const keys = Object.entries(preview.diffs)
    .filter(([, v]) => v.state !== "unchanged")
    .map(([k]) => k);

  return (
    <div className="border border-border/50 bg-secondary/10" data-testid="mod-diff-preview">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 p-2.5 text-left hover:bg-secondary/20 transition-colors focus:outline-none focus:ring-1 focus:ring-current"
        data-testid="mod-diff-toggle"
        aria-expanded={expanded}
        aria-controls="mod-diff-panel"
      >
        <div className="label-cap">Live Diff Preview</div>
        <span className={`chip !text-[10px] uppercase border-current ${verdictTone}`} data-testid={`mod-diff-verdict-${preview.verdict_state}`}>
          {preview.before.risk_pass ? <CheckCircle2 size={10} className="inline mr-1"/> : <XCircle size={10} className="inline mr-1"/>}
          {verdictLabel}
        </span>
        <div className="ml-auto text-[10px] mono text-muted-foreground">
          {keys.length} field{keys.length !== 1 ? "s" : ""} change
        </div>
        {expanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
      </button>

      {expanded && (
        <div id="mod-diff-panel" className="border-t border-border/40 p-3 space-y-2">
          <table className="w-full text-xs">
            <caption className="sr-only">Before-and-after diff of the suggested modification</caption>
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left font-normal pb-1">Field</th>
                <th className="text-right font-normal pb-1">Before</th>
                <th className="w-4"></th>
                <th className="text-right font-normal pb-1">After</th>
                <th className="text-right font-normal pb-1">Change</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const d = preview.diffs[k];
                const b = preview.before[k], a = preview.after[k];
                const change = (typeof b === "number" && typeof a === "number") ? a - b : null;
                return (
                  <tr key={k} className="border-b border-border/20 last:border-0" data-testid={`mod-diff-row-${k}`}>
                    <td className="py-1.5 mono text-[11px]">{FIELD_LABEL[k] || k}</td>
                    <td className="py-1.5 mono text-right text-muted-foreground line-through decoration-1">{fmt(k, b)}</td>
                    <td className="text-center text-muted-foreground" aria-hidden="true"><ArrowRight size={10}/></td>
                    <td className={`py-1.5 mono text-right ${STATE_TONE[d.state] || ""}`}>{fmt(k, a)}</td>
                    <td className={`py-1.5 mono text-right ${STATE_TONE[d.state] || ""} text-[10px]`}>
                      {change !== null && Math.abs(change) > 0.001
                        ? `${change > 0 ? "+" : ""}${fmt(k, change)}`
                        : ""}
                    </td>
                  </tr>
                );
              })}
              {/* Compliance row is always shown for context */}
              <tr className="border-t border-border/40" data-testid="mod-diff-row-compliance">
                <td className="py-1.5 mono text-[11px] uppercase text-muted-foreground">Risk Engine</td>
                <td className={`py-1.5 mono text-right text-[10px] uppercase ${preview.before.risk_pass ? "pnl-up" : "pnl-down"}`}>
                  {preview.before.risk_pass ? "Approved" : "Rejected"}
                </td>
                <td className="text-center text-muted-foreground" aria-hidden="true"><ArrowRight size={10}/></td>
                <td className={`py-1.5 mono text-right text-[10px] uppercase ${preview.after.risk_pass ? "pnl-up" : "pnl-down"}`}>
                  {preview.after.risk_pass ? "Approved" : "Rejected"}
                </td>
                <td className={`py-1.5 mono text-right text-[10px] uppercase ${verdictTone}`}>{verdictLabel}</td>
              </tr>
            </tbody>
          </table>
          {preview.after.risk_summary?.failing_checks?.length > 0 && (
            <div className="text-[10px] mono uppercase text-muted-foreground">
              Still failing after modification: <span className="pnl-down">{preview.after.risk_summary.failing_checks.join(" · ")}</span>
            </div>
          )}
          {onApply && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={onApply}
                className="chip pnl-up border-current text-[10px] hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-current"
                data-testid="mod-diff-apply"
              >
                <CheckCircle2 size={11} className="inline mr-1"/>Apply Modification
              </button>
              {onReject && (
                <button
                  onClick={onReject}
                  className="chip border-current text-muted-foreground text-[10px] hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current"
                  data-testid="mod-diff-reject"
                >Reject Suggestion</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
